import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Wrench, Users, Package, FileText, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { BadgeEstado } from './ui';

const TIPO_CONFIG = {
  reparacion:   { icono: Wrench,    color: 'text-blue-500',   bg: 'bg-blue-50',   label: 'Reparación', ruta: '/reparaciones' },
  cliente:      { icono: Users,     color: 'text-green-500',  bg: 'bg-green-50',  label: 'Cliente',    ruta: '/clientes' },
  producto:     { icono: Package,   color: 'text-orange-500', bg: 'bg-orange-50', label: 'Producto',   ruta: '/inventario' },
  presupuesto:  { icono: FileText,  color: 'text-purple-500', bg: 'bg-purple-50', label: 'Presupuesto',ruta: '/presupuestos' },
  venta:        { icono: FileText,  color: 'text-emerald-500',bg: 'bg-emerald-50',label: 'Venta',      ruta: '/ventas' },
};

export default function BusquedaGlobal({ onClose }) {
  const [query, setQuery]         = useState('');
  const [resultados, setResultados] = useState(null);
  const [cargando, setCargando]   = useState(false);
  const [seleccionado, setSeleccionado] = useState(0);
  const [escaneando, setEscaneando] = useState(false);
  const [errorCam, setErrorCam] = useState('');
  const inputRef = useRef(null);
  const scannerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Escáner de cámara (QR + códigos de barras) con html5-qrcode
  const abrirCamara = async () => {
    setErrorCam('');
    setEscaneando(true);
  };

  useEffect(() => {
    if (!escaneando) return;
    let cancelado = false;
    (async () => {
      try {
        // Cargar html5-qrcode desde CDN si no está
        if (!window.Html5Qrcode) {
          await new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html5-qrcode/2.3.8/html5-qrcode.min.js';
            s.onload = res; s.onerror = rej;
            document.head.appendChild(s);
          });
        }
        if (cancelado) return;
        const Html5Qrcode = window.Html5Qrcode;
        const scanner = new Html5Qrcode('lector-camara');
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 180 } },
          (texto) => {
            // Código detectado: rellenar búsqueda y cerrar cámara
            const limpio = String(texto).replace(/^R-?/i, '').trim();
            setQuery(texto.includes('R-') || texto.includes('R') ? texto : limpio || texto);
            cerrarCamara();
          },
          () => {}
        );
      } catch (e) {
        if (!cancelado) { setErrorCam('No se pudo abrir la cámara. Permite el acceso o escribe el código a mano.'); setEscaneando(false); }
      }
    })();
    return () => { cancelado = true; };
  }, [escaneando]);

  const cerrarCamara = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().then(() => scannerRef.current.clear()).catch(()=>{});
      scannerRef.current = null;
    }
    setEscaneando(false);
  };
  useEffect(() => () => cerrarCamara(), []);

  // Búsqueda con debounce
  useEffect(() => {
    if (query.length < 2) { setResultados(null); return; }
    const timer = setTimeout(async () => {
      setCargando(true);
      try {
        // Normalizar entrada de escáner de código de barras (teclado US/ES: ' en vez de -)
        const qNorm = query.replace(/['´`’]/g, '-');
        const { data } = await api.get('/busqueda', { params: { q: qNorm } });
        const todos = [
          ...data.data.reparaciones.map(r => ({...r, tipo:'reparacion'})),
          ...data.data.clientes.map(c => ({...c, tipo:'cliente'})),
          ...data.data.productos.map(p => ({...p, tipo:'producto'})),
          ...data.data.presupuestos.map(p => ({...p, tipo:'presupuesto'})),
          ...(data.data.ventas||[]).map(v => ({...v, tipo:'venta'})),
        ];
        setResultados(todos);
        setSeleccionado(0);
      } catch { setResultados([]); }
      finally { setCargando(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Navegación con teclado
  useEffect(() => {
    const handler = (e) => {
      if (!resultados?.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setSeleccionado(s => Math.min(s+1, resultados.length-1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSeleccionado(s => Math.max(s-1, 0)); }
      if (e.key === 'Enter')     { e.preventDefault(); irA(resultados[seleccionado]); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [resultados, seleccionado]);

  const irA = (item) => {
    const cfg = TIPO_CONFIG[item.tipo];
    // Abrir directamente la ficha del elemento (escáner o clic)
    const conFicha = ['reparacion', 'cliente', 'producto', 'venta'];
    if (item.id && conFicha.includes(item.tipo)) {
      navigate(`${cfg.ruta}?abrir=${item.id}`);
    } else {
      navigate(cfg.ruta);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100" onClick={e=>e.stopPropagation()}>

        {/* Input búsqueda */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <Search size={18} className="text-gray-400 flex-shrink-0"/>
          <input ref={inputRef} value={query} onChange={e=>setQuery(e.target.value)}
            className="flex-1 text-base text-gray-800 placeholder-gray-400 outline-none bg-transparent"
            placeholder="Buscar reparaciones, clientes, productos..."/>
          {query && <button onClick={()=>setQuery('')} className="text-gray-400 hover:text-gray-600"><X size={16}/></button>}
          <button onClick={escaneando ? cerrarCamara : abrirCamara} title="Escanear con cámara"
            className={escaneando ? 'text-red-500 flex-shrink-0' : 'text-blue-600 hover:text-blue-800 flex-shrink-0'}>
            <Camera size={20}/>
          </button>
          <kbd className="hidden sm:flex items-center gap-1 text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">Esc</kbd>
        </div>

        {/* Lector de cámara */}
        {escaneando && (
          <div className="px-4 py-3 bg-gray-900">
            <div id="lector-camara" className="w-full rounded-xl overflow-hidden mx-auto" style={{maxWidth:'400px'}}></div>
            <p className="text-center text-xs text-gray-300 mt-2">Apunta al QR o código de barras del ticket/producto</p>
            <button onClick={cerrarCamara} className="mx-auto mt-2 block text-xs text-gray-400 hover:text-white">Cerrar cámara</button>
          </div>
        )}
        {errorCam && <div className="px-4 py-2 bg-red-50 text-xs text-red-600">{errorCam}</div>}

        {/* Resultados */}
        <div className="max-h-96 overflow-y-auto">
          {!query && (
            <div className="px-4 py-8 text-center">
              <Search size={32} className="text-gray-200 mx-auto mb-3"/>
              <p className="text-gray-400 text-sm">Escribe para buscar en todo el sistema</p>
              <p className="text-gray-300 text-xs mt-1">Reparaciones · Clientes · Productos · Presupuestos</p>
            </div>
          )}

          {cargando && (
            <div className="px-4 py-6 text-center">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"/>
            </div>
          )}

          {resultados && !cargando && resultados.length === 0 && (
            <div className="px-4 py-8 text-center">
              <p className="text-gray-400 text-sm">Sin resultados para "{query}"</p>
            </div>
          )}

          {resultados && !cargando && resultados.length > 0 && (
            <div className="py-2">
              {/* Agrupar por tipo */}
              {['reparacion','cliente','producto','presupuesto','venta'].map(tipo => {
                const items = resultados.filter(r => r.tipo === tipo);
                if (!items.length) return null;
                const cfg = TIPO_CONFIG[tipo];
                const Icono = cfg.icono;
                return (
                  <div key={tipo}>
                    <p className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50">{cfg.label}s</p>
                    {items.map((item, idx) => {
                      const globalIdx = resultados.indexOf(item);
                      return (
                        <button key={item.id} onClick={() => irA(item)}
                          className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-all text-left ${globalIdx===seleccionado?'bg-blue-50':''}`}>
                          <div className={`w-9 h-9 ${cfg.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <Icono size={16} className={cfg.color}/>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {item.ref && <span className="text-xs font-mono text-gray-400">{item.ref}</span>}
                              {item.estado && <BadgeEstado estado={item.estado}/>}
                            </div>
                            <p className="font-medium text-gray-800 text-sm truncate">{item.titulo}</p>
                            {item.subtitulo && <p className="text-xs text-gray-400 truncate">{item.subtitulo}</p>}
                            {item.cliente && item.tipo !== 'cliente' && <p className="text-xs text-gray-400">👤 {item.cliente} {item.telefono && `· ${item.telefono}`}</p>}
                          </div>
                          <span className="text-xs text-gray-300 flex-shrink-0">↵</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1"><kbd className="bg-white border border-gray-200 rounded px-1">↑↓</kbd> Navegar</span>
          <span className="flex items-center gap-1"><kbd className="bg-white border border-gray-200 rounded px-1">↵</kbd> Abrir</span>
          <span className="flex items-center gap-1"><kbd className="bg-white border border-gray-200 rounded px-1">Esc</kbd> Cerrar</span>
        </div>
      </div>
    </div>
  );
}
